// Off-screen pointers (docs/SPEC.md item 4; port of web/pointers.js): this tab's matches just off the visible map
// get their pin at the edge (same fill, outline and shape) with a chevron toward them, the best few (open things and
// interest matches first, nearest first, one per direction), never where a pin, group, your dot or a control is
// (they fade in and out instead). Plain DOM owned by the map side (docs/ARCHITECTURE.md §2.5): at most maxPointers
// elements, re-placed at most 10×/s while the map moves.
import type * as GeoJSON from "geojson";
import { timed } from "../perf";
import { SHARED } from "../shared/config";
import { RULES, type Level } from "../shared/rules";
import { clockStore } from "../state/clock";
import { emojiOf, eventById, interestLevelOf } from "../state/feed";
import { filtered } from "../state/filtered";
import { profileStore } from "../state/profile";
import { moveMap, openSheet, selectedId, viewStore } from "../state/view";
import type { FeedEvent } from "../data/event";
import { cssColor, GROUP_MAX, paintEmojiGlyph, PIN_SIZE, rankMember, shapePoints, shapeSVG } from "./draw";
import type { MapController } from "./MapController";

const POINTER_R = SHARED.pins.pointer.radius, CHEVRON = SHARED.pins.pointer, POINTER_EMOJI = 15;
const FADE = SHARED.pointerFadeMs, CLEAR = SHARED.pointerClearance, MAX = SHARED.maxPointers;
const OBSTACLE_LAYERS = ["primary-pins", "primary-clusters", "secondary-pins", "thin-pins", "me"];
const EVERY = 100;

type Shape = "squircle" | "ticket";
const reachOf = (shape: Shape, a: number) => Math.max(...shapePoints(shape, POINTER_R).map(([x, y]) => x * Math.cos(a) + y * Math.sin(a)));
const chevronPaths = new Map<string, string>();
function chevronPath(shape: Shape, angle: number) {
  const step = Math.round(angle / (Math.PI / 180)), key = `${shape}|${step}`;
  let p = chevronPaths.get(key);
  if (!p) {
    const a = (step * Math.PI) / 180, arm = CHEVRON.chevronSize, d = reachOf(shape, a) + CHEVRON.chevronGap + arm * Math.SQRT1_2;
    const pt = (b: number, len: number) => `${(Math.cos(a) * d + Math.cos(b) * len).toFixed(2)} ${(Math.sin(a) * d + Math.sin(b) * len).toFixed(2)}`;
    p = `M${pt(a + Math.PI * 0.75, arm)}L${pt(a, 0)}L${pt(a - Math.PI * 0.75, arm)}`;
    chevronPaths.set(key, p);
  }
  return p;
}
const TIP = Math.max(...(["squircle", "ticket"] as const).map((s) => reachOf(s, Math.PI / 4))) +
  CHEVRON.chevronGap + CHEVRON.chevronSize * Math.SQRT1_2 + CHEVRON.chevronWidth;

// Web Mercator, 0…1 across the world (MapLibre's MercatorCoordinate), cached per event.
const mercator = ({ lng, lat }: { lng: number; lat: number }): [number, number] =>
  [(lng + 180) / 360, (180 - (180 / Math.PI) * Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360))) / 360];
const mercators = new WeakMap<FeedEvent, [number, number]>();
function mercatorOf(e: FeedEvent) {
  let m = mercators.get(e);
  if (!m) { m = mercator(e); mercators.set(e, m); }
  return m;
}

interface Rect { left: number; right: number; top: number; bottom: number; mapLeft: number; mapTop: number }
interface Obstacle { x: number; y: number; size: number }
const rectOf = (sel: string) => document.querySelector(sel)?.getBoundingClientRect() ?? null;

export class Pointers {
  readonly layer: HTMLDivElement;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private last = 0;
  private geometry: { r: Rect | null; controls: DOMRect[] } | null = null;

  constructor(private readonly ctl: MapController) {
    this.layer = document.createElement("div");
    this.layer.id = "pointers";
    this.layer.setAttribute("aria-label", "Events just off the map");
    document.body.append(this.layer);
    addEventListener("resize", this.onResize);
  }
  destroy() { removeEventListener("resize", this.onResize); this.layer.remove(); }
  private onResize = () => this.update();

  /** While the map moves: at most every EVERY ms (their CSS transition smooths the steps). */
  updateSoon() {
    if (this.timer) return;
    const wait = Math.max(0, EVERY - (performance.now() - this.last));
    this.timer = setTimeout(() => { this.timer = null; this.update(true); }, wait);
  }

  // null when the visible map is small (phone width: the list or a card pulled up, or another sheet over the map).
  private visibleRect(): Rect | null {
    const m = this.ctl.map.getContainer().getBoundingClientRect(), vs = viewStore.getState();
    let top = m.top, bottom = m.bottom;
    const controls = rectOf("#controls");
    if (controls && controls.bottom > top && controls.right > m.left + 10) top = Math.max(top, controls.bottom); // phone: floating panel
    const sr = rectOf("#sheet"), nb = rectOf("#nearby");
    const sheetOverMap = !!sr && sr.height > 0 && sr.right > m.left + 10;
    if (sheetOverMap && !(vs.sheet?.kind === "event" && !vs.sheet.full)) return null;
    if (!sheetOverMap && vs.nearbyExpanded && nb && nb.height && nb.right > m.left + 10) return null;
    if (sheetOverMap) bottom = Math.min(bottom, sr.top);
    else if (nb && nb.height && nb.right > m.left + 10) bottom = Math.min(bottom, nb.top) - 70; // phone: list bar + buttons
    else bottom -= 70; // the round buttons
    return { left: m.left + 4, right: m.right - 4, top: top + 4, bottom: bottom - 4, mapLeft: m.left, mapTop: m.top };
  }

  private pageGeometry() {
    const controls = [...document.querySelectorAll<HTMLElement>("#controls, [data-map-control], .maplibregl-ctrl-top-right")]
      .filter((el) => el.offsetParent).map((el) => el.getBoundingClientRect());
    return { r: this.visibleRect(), controls };
  }

  // Everything drawn where pointers can go: four rendered-feature queries (one strip per edge), once per update.
  private edgeObstacles(r: Rect): Obstacle[] {
    const map = this.ctl.map, layers = OBSTACLE_LAYERS.filter((l) => map.getLayer(l));
    const band = 2 * TIP + CLEAR + Math.max(GROUP_MAX, ...Object.values(PIN_SIZE)) / 2;
    const L = r.left - r.mapLeft, R = r.right - r.mapLeft, T = r.top - r.mapTop, B = r.bottom - r.mapTop;
    const strips: [[number, number], [number, number]][] = [[[L, T], [R, T + band]], [[L, B - band], [R, B]], [[L, T], [L + band, B]], [[R - band, T], [R, B]]];
    const seen = new Set<string>(), out: Obstacle[] = [];
    for (const box of strips) {
      for (const f of map.queryRenderedFeatures(box, { layers })) {
        const [lng, lat] = (f.geometry as GeoJSON.Point).coordinates as [number, number], k = `${f.layer.id}|${lng}|${lat}`;
        if (seen.has(k)) continue;
        seen.add(k);
        const p = map.project([lng, lat]), level = String(f.properties?.icon ?? "").split("|")[1] as Level;
        out.push({ x: p.x, y: p.y, size: f.layer.id === "me" ? 20
          : f.properties?.point_count ? RULES.groupSize(rankMember(Number(f.properties.best)).level) : PIN_SIZE[level] ?? PIN_SIZE.none });
      }
    }
    return out;
  }

  // This tab's matches off the visible area but within `reach`: with the map flat and north-up, two boxes in degrees,
  // so each event costs two comparisons; only those between them are projected.
  private candidates(r: Rect, reach: number) {
    const map = this.ctl.map, selId = selectedId(), interests = profileStore.getState().view.interests;
    const flat = map.getBearing() === 0 && map.getPitch() === 0;
    const box = (pad: number) => {
      const a = map.unproject([r.left - r.mapLeft - pad, r.top - r.mapTop - pad]);
      const b = map.unproject([r.right - r.mapLeft + pad, r.bottom - r.mapTop + pad]);
      return { w: a.lng, e: b.lng, n: a.lat, s: b.lat };
    };
    const inner = flat ? box(0) : null, outer = flat ? box(reach) : null;
    // Flat and north-up, a point's screen position is its Web Mercator position (worked out once per event) scaled
    // about the centre: two multiplies instead of a projection per event.
    const c = map.getCenter(), cm = mercator(c), ws = 512 * 2 ** map.getZoom(), cont = map.getContainer();
    const half = { x: cont.clientWidth / 2, y: cont.clientHeight / 2 };
    const screen = flat
      ? (e: FeedEvent) => { const m = mercatorOf(e); return { x: (m[0] - cm[0]) * ws + half.x, y: (m[1] - cm[1]) * ws + half.y }; }
      : (e: FeedEvent) => map.project([e.lng, e.lat]);
    const out: { e: FeedEvent; x: number; y: number; out: number; tier: number }[] = [];
    for (const e of filtered().primary) {
      if (e.id === selId) continue;
      if (inner && outer) {
        if (e.lng < outer.w || e.lng > outer.e || e.lat > outer.n || e.lat < outer.s) continue;
        if (e.lng >= inner.w && e.lng <= inner.e && e.lat <= inner.n && e.lat >= inner.s) continue;
      }
      const p = screen(e), x = p.x + r.mapLeft, y = p.y + r.mapTop;
      if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) continue;
      const dist = Math.hypot(Math.max(r.left - x, 0, x - r.right), Math.max(r.top - y, 0, y - r.bottom));
      if (dist > reach) continue;
      const tier = RULES.openness(e) === "open" || interestLevelOf(e, interests) === "interest" ? 0 : 1;
      out.push({ e, x, y, out: dist, tier });
    }
    return out;
  }

  private remove(el: HTMLElement) {
    if (el.dataset.leaving) return;
    el.dataset.leaving = "1";
    el.style.pointerEvents = "none";
    el.style.opacity = "0";
    setTimeout(() => el.remove(), FADE);
  }

  update(moving = false) {
    if (this.ctl.ready) timed("pointers", () => this.place(moving));
  }
  private place(moving: boolean) {
    this.last = performance.now();
    if (!moving || !this.geometry) this.geometry = this.pageGeometry();
    const { r, controls } = this.geometry, now = clockStore.getState().now;
    const w = r ? r.right - r.left : 0, hgt = r ? r.bottom - r.top : 0;
    if (!r || w < 80 || hgt < 80) { [...this.layer.children].forEach((el) => this.remove(el as HTMLElement)); return; }
    const cx = (r.left + r.right) / 2, cy = (r.top + r.bottom) / 2, reach = Math.max(w, hgt) * 1.5;
    const cands = this.candidates(r, reach).sort((a, b) => a.tier - b.tier || a.out - b.out);
    let found: Obstacle[] | null = null;
    const obstacles = () => (found ??= this.edgeObstacles(r));
    const pad = TIP + CLEAR, bw = w / 2 - TIP, bh = hgt / 2 - TIP;
    const blocked = (x: number, y: number) => {
      if (controls.some((c) => x + pad > c.left && x - pad < c.right && y + pad > c.top && y - pad < c.bottom)) return true;
      const bx = x - r.mapLeft, by = y - r.mapTop;
      return obstacles().some((o) => Math.abs(o.x - bx) < pad + o.size / 2 && Math.abs(o.y - by) < pad + o.size / 2);
    };
    const byDir = new Map<number, { e: FeedEvent; angle: number; px: number; py: number }>();
    for (const c of cands) {
      const angle = Math.atan2(c.y - cy, c.x - cx);
      const octant = Math.floor(((angle + Math.PI / 8 + 2 * Math.PI) % (2 * Math.PI)) / (Math.PI / 4)) % 8;
      if (byDir.has(octant)) continue;
      const dx = Math.cos(angle), dy = Math.sin(angle);
      const t = Math.min(Math.abs(dx) > 1e-4 ? bw / Math.abs(dx) : Infinity, Math.abs(dy) > 1e-4 ? bh / Math.abs(dy) : Infinity);
      const px = cx + dx * t, py = cy + dy * t;
      if (blocked(px, py)) continue;
      byDir.set(octant, { e: c.e, angle, px, py });
      if (byDir.size === MAX) break;
    }
    const existing = new Map([...this.layer.children].map((el) => el as HTMLElement).filter((el) => !el.dataset.leaving).map((el) => [el.dataset.id!, el]));
    const keep = new Set<string>();
    for (const c of byDir.values()) {
      let el = existing.get(c.e.id) as HTMLButtonElement | undefined;
      const fresh = !el;
      if (!el) {
        // New (or back after being too close to something): fade in, never pop.
        el = document.createElement("button");
        el.className = "pointer";
        el.dataset.id = c.e.id;
        const id = c.e.id;
        el.addEventListener("click", () => tap(id));
        el.innerHTML = `<svg class="bubble" viewBox="-28 -28 56 56" aria-hidden="true"><g class="body"></g><path class="chevron" fill="none" stroke-width="${CHEVRON.chevronWidth}" stroke-linecap="round" stroke-linejoin="round"/></svg><span class="pe"><canvas class="emoji-glyph"></canvas></span>`;
        el.style.transition = `transform .08s linear, opacity ${FADE}ms`;
        el.style.opacity = "0";
        this.layer.append(el);
      }
      el.title = c.e.title;
      el.setAttribute("aria-label", `Off screen: ${c.e.title}`);
      const glyph = el.querySelector("canvas")!, emoji = emojiOf(c.e);
      if (glyph.dataset.emoji !== emoji) { glyph.dataset.emoji = emoji; paintEmojiGlyph(glyph, emoji, POINTER_EMOJI); }
      const style = RULES.pinStyle(c.e, now), body = el.querySelector<SVGGElement>(".bubble .body")!, look = `${style.outline}|${style.shape}`;
      if (body.dataset.look !== look) { body.dataset.look = look; body.innerHTML = shapeSVG(style, POINTER_R); }
      const chevron = el.querySelector(".bubble .chevron")!, d = chevronPath(style.shape, c.angle);
      if (chevron.getAttribute("d") !== d) chevron.setAttribute("d", d);
      const stroke = cssColor(RULES.pointerChevron(style.outline));
      if (chevron.getAttribute("stroke") !== stroke) chevron.setAttribute("stroke", stroke);
      const opacity = String(RULES.pinOpacity(c.e, now)), target = el;
      if (fresh) requestAnimationFrame(() => { target.style.opacity = opacity; });
      else el.style.opacity = opacity;
      el.style.transform = `translate(${c.px - 14}px, ${c.py - 14}px)`;
      keep.add(c.e.id);
    }
    for (const [id, el] of existing) if (!keep.has(id)) this.remove(el);
  }
}

// Tapping one pans to centre its event and opens its card.
function tap(id: string) {
  const e = eventById(id);
  if (!e) return;
  moveMap({ lat: e.lat, lng: e.lng });
  openSheet({ kind: "event", id });
}
